import React, { useEffect, useState } from "react";
import styled, { css, keyframes } from "styled-components";
import { useNavigate } from "react-router-dom";
import { palette } from "@/styles/palette";
import { Review, fetchRecentReviews } from "@/api/reviewApi";
import { API } from "@/constants/api";

const RecentReviews = () => {
    const [reviews, setReviews] = useState<Review[]>([]);
    const [currentGroup, setCurrentGroup] = useState(0);
    const navigate = useNavigate();
    const itemsPerGroup = 3;

    useEffect(() => {
        const loadReviews = async () => {
            try {
                const data = await fetchRecentReviews();
                setReviews(data.slice(0, 10));
            } catch (error) {
                console.error("리뷰 데이터를 불러오는데 실패했습니다:", error);
            }
        };

        loadReviews();
    }, []);

    useEffect(() => {
        if (reviews.length <= itemsPerGroup) return;

        const interval = setInterval(() => {
            setCurrentGroup((prev) => (prev + 1) % Math.ceil(reviews.length / itemsPerGroup));
        }, 2000);

        return () => clearInterval(interval);
    }, [reviews.length]);

    const getCurrentReviews = () => {
        const startIndex = currentGroup * itemsPerGroup;
        const currentSlice = reviews.slice(startIndex, startIndex + itemsPerGroup);

        // 리뷰가 3개 미만인 경우 있는 만큼만 반환
        if (reviews.length < itemsPerGroup) {
            return reviews;
        }

        // 리뷰가 3개 이상일 때만
        if (currentSlice.length < itemsPerGroup) {
            const remainingCount = itemsPerGroup - currentSlice.length;
            const remainingItems = reviews.slice(0, remainingCount);
            return [...currentSlice, ...remainingItems];
        }

        return currentSlice;
    };

    const handleReviewClick = (productAltKey: string) => {
        navigate(`/alcohol/${productAltKey}`);
    };

    if (reviews.length === 0) return null;

    return (
        <ReviewSection>
            <SectionTitle>최근 리뷰</SectionTitle>
            <ReviewList>
                {getCurrentReviews().map((review) => (
                    <ReviewItem key={review.altKey} onClick={() => handleReviewClick(review.product.altKey)}>
                        <ReviewImageWrapper>
                            {review.reviewImageInfoList[0] ? (
                                <ReviewImage
                                    src={`${API.IMAGE_DOMAIN}/${review.reviewImageInfoList[0].imageUrl}`}
                                    alt="리뷰 이미지"
                                />
                            ) : (
                                <DefaultImage>
                                    <img src="/images/default-image.png" alt="기본 이미지" />
                                </DefaultImage>
                            )}
                        </ReviewImageWrapper>
                        <ReviewInfo>
                            <ReviewTop>
                                <ProductName>{review.product.name}</ProductName>
                                <RatingBadge>
                                    <img src="/icon/star.png" alt="별점" width="12" height="12" />
                                    <span>{review.rating}</span>
                                </RatingBadge>
                            </ReviewTop>
                            <ReviewContent>{review.content}</ReviewContent>
                        </ReviewInfo>
                    </ReviewItem>
                ))}
            </ReviewList>
        </ReviewSection>
    );
};

export default RecentReviews;

const slideIn = keyframes`
    from {
        opacity: 0;
        transform: translateY(-10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const slideOut = keyframes`
    from {
        opacity: 1;
        transform: translateY(0);
    }
    to {
        opacity: 0;
        transform: translateY(20px);
    }
`;

const ReviewSection = styled.section`
    padding: 0 16px;
    margin-bottom: 24px;
    width: 100%;
`;

const SectionTitle = styled.h2`
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 12px;
    color: ${palette.gray[900]};
`;

const ReviewList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-height: 216px;
`;

const ReviewItem = styled.div`
    display: flex;
    gap: 12px;
    padding: 12px;
    background: ${palette.white};
    border-radius: 8px;
    cursor: pointer;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    transition: all 0.4s ease;
    animation: ${slideIn} 0.7s ease forwards;

    &:hover {
        transform: translateX(4px);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }
`;

const ReviewImageWrapper = styled.div`
    width: 80px;
    height: 80px;
    flex-shrink: 0;
    border-radius: 8px;
    overflow: hidden;
`;

const ReviewImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 8px;
`;

const DefaultImage = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${palette.gray[100]};
    border-radius: 8px;
    overflow: hidden;

    img {
        width: 60%;
        height: 60%;
        object-fit: contain;
    }
`;

const ReviewInfo = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
`;

const ReviewTop = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
`;

const ProductName = styled.h3`
    font-size: 14px;
    font-weight: 600;
    color: ${palette.gray[900]};
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1;
    min-width: 0;
`;

const RatingBadge = styled.div`
    display: flex;
    align-items: center;
    gap: 2px;
    padding: 2px 6px;
    background: #ffc700;
    border-radius: 12px;
    flex-shrink: 0;

    span {
        color: white;
        font-size: 12px;
        font-weight: 600;
    }
`;

const ReviewContent = styled.p`
    font-size: 13px;
    line-height: 140%;
    color: ${palette.gray[700]};
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
`;
